import { useState, useEffect } from "react";
import { Trash2, Pencil, Plus } from "lucide-react";
import axios from "axios";
import Banner from "../../components/Banner";

const ITEMS_PER_PAGE = 8;

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    name: "",
    price: "",
    gender: "unisex",
    image: null,
    category: "accessory",
    description: "",
    descriptionLong: "",
    tag: "",
    featured: false,
    inStock: true,
    stockQty: "",
  });

  const [editIndex, setEditIndex] = useState(null);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('image', file);

    const res = await axios.post(
      `${BASE_URL}/api/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );

    return {
      image: res.data.image,             // thumbnail path
      imageHighRes: res.data.imageHighRes, // original image path
    };
  };


  // Collapsible form toggle
  const [showForm, setShowForm] = useState(false);

  // Filters & search
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess("");
        setError("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [success, error]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await axios.get(`${BASE_URL}/api/products`, {
          withCredentials: true, // ✅ send HttpOnly cookie
        });

        setProducts(data);
      } catch (err) {
        console.error("Failed to load products:", err);
      }
    };

    fetchProducts();
  }, [BASE_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    setError("");

    try {
      let imageUrl = form.image;
      let imageHighResUrl = form.imageHighRes;

      if (form.image instanceof File) {
        const { image, imageHighRes } = await uploadImage(form.image);
        imageUrl = image;
        imageHighResUrl = imageHighRes;
      }

      const payload = {
        ...form,
        price: Number(form.price),
        image: imageUrl,
        imageHighRes: imageHighResUrl,
        inStock: form.stockQty > 0,
      };

      if (editIndex !== null) {
        const { data: updated } = await axios.put(
          `${BASE_URL}/api/products/${products[editIndex]._id}`,
          payload,
          { withCredentials: true } // ✅ include the login cookie
        );
        const newList = [...products];
        newList[editIndex] = updated;
        setProducts(newList);
        setSuccess("Product updated successfully!");
      } else {
        const { data: newProduct } = await axios.post(
          `${BASE_URL}/api/products`,
          payload,
          { withCredentials: true } // ✅ include the login cookie
        );
        setProducts([newProduct, ...products]);
        setSuccess("Product created successfully!");
      }

      resetForm();
      setShowForm(false);
    } catch (err) {
      console.error("❌ Failed to save product:", err);
      setError("Failed to save product. Please check your inputs and try again.");
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      price: "",
      gender: "unisex",
      image: null,
      category: "accessory",
      description: "",
      descriptionLong: "",
      tag: "",
      featured: false,
      inStock: true,
      stockQty: "",
    });
    setEditIndex(null);
  };

  const handleEdit = (index) => {
    const product = products[index];

    setForm({
      ...product,
      image: product.image, // keep image as string for preview
      imageHighRes: product.imageHighRes,
    });

    // This ensures the file input shows the file name (visually, not natively)
    setEditIndex(index);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };


  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axios.delete(`${BASE_URL}/api/products/${id}`, {
          withCredentials: true, // ✅ include login cookie
        });
        setProducts((prev) => prev.filter((p) => p._id !== id));
        setSuccess("Product deleted successfully.");
      } catch (error) {
        console.error('Delete failed:', error);
        setError("Failed to delete product. Please try again.");
      }
    }
  };

  // Filtering & searching
  const filtered = products.filter((p) => {
    const matchesSearch =
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.description && p.description.toLowerCase().includes(search.toLowerCase()));

    const matchesCategory = categoryFilter === "All" || p.category === categoryFilter;

    const matchesStatus =
      statusFilter === "All" ||
      (statusFilter === "In Stock" ? p.inStock : !p.inStock);

    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <Banner type="success" message={success} />
        <Banner type="error" message={error} />

        <h1 className="text-2xl font-bold">
          {editIndex !== null ? "Edit Product" : "All Products"}
        </h1>

        <button
          onClick={() => {
            setShowForm((show) => !show);
            resetForm();
            setEditIndex(null);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500 flex items-center gap-2"
        >
          {showForm ? (
            "Hide Form"
          ) : (
            <>
              <Plus /> Add New Product
            </>
          )}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow mb-10">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-2 font-medium">
                Product Name
              </label>
              <input
                required
                type="text"
                placeholder="Product Name"
                className="border p-2 rounded w-full"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label className="block mb-2 font-medium">
                Price
              </label>
              <input
                required
                type="number"
                placeholder="Price"
                step="0.01"
                min={0}
                className="border p-2 rounded w-full"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </div>
            <div>
              <label className="block mb-2 font-medium">
                Stock Quantity
              </label>
              <input
                required
                type="number"
                placeholder="Stock Quantity"
                min={0}
                className="border p-2 rounded w-full"
                value={form.stockQty}
                onChange={(e) => {
                  const qty = Number(e.target.value);
                  setForm({
                    ...form,
                    stockQty: qty,
                    inStock: qty > 0,
                  });
                }}
              />
            </div>
            {/* Category first */}
            <div>
              <label className="block mb-2 font-medium">
                Category
              </label>
              <select
                className="border p-2 rounded w-full"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                <option value="accessory">Accessory</option>
                <option value="flower">Flower</option>
                <option value="vip">Vip</option>
              </select>
            </div>

            {/* Gender only if not flower */}
            {form.category !== "flower" && (
              <div>
                <label className="block mb-2 font-medium">
                  Gender
                </label>
                <select
                  className="border p-2 rounded w-full"
                  value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}
                >
                  <option value="unisex">Unisex</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div> 
            )}

            <div>
              {/* Image Upload */}
              <div>
                <label className="block mb-2 font-medium">
                  Upload Image
                </label>
                <div className="mb-2">
                  <input
                    id="fileInput"
                    type="file"
                    accept="image/png, image/jpeg, image/jpg, image/svg+xml"
                    className="border p-2 rounded w-full"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setForm((prev) => ({ ...prev, image: file }));
                      }
                    }}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {typeof form.image === "string"
                      ? `Current Image: ${form.image.split("/").pop()}`
                      : form.image
                      ? `Selected: ${form.image.name}`
                      : "No file chosen"}
                  </p>
                </div>

                {form.image && (
                  <img
                    src={
                      typeof form.image === "string"
                        ? form.image
                        : URL.createObjectURL(form.image)
                    }
                    alt="Product preview"
                    className="mt-2 h-40 w-40 rounded object-cover border"
                  />
                )}
              </div>
            </div>
          </div>

          {/* SHORT DESCRIPTION (limit: 100) */}
          <div>
            <label className="block mb-2 mt-4 font-medium">Short Description</label>
            <input
              type="text"
              placeholder="Short Description"
              className="border p-2 rounded w-full mb-1"
              maxLength={100}
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  description: e.target.value.length <= 100
                    ? e.target.value
                    : prev.description
                }))
              }
            />
            <p className="text-sm text-gray-500 text-right mb-4">
              {form.description.length}/100
            </p>
          </div>

          {/* LONG DESCRIPTION (limit: 500) */}
          <div>
            <label className="block mb-2 font-medium">Long Description</label>
            <textarea
              placeholder="Long Description"
              className="border p-2 rounded w-full mb-1"
              maxLength={500}
              value={form.descriptionLong}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  descriptionLong: e.target.value.length <= 500
                    ? e.target.value
                    : prev.descriptionLong
                }))
              }
              rows={4}
            />
            <p className="text-sm text-gray-500 text-right mb-4">
              {form.descriptionLong.length}/500
            </p>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:space-x-14 space-y-2 md:space-y-0 mb-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              Featured Product
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.inStock}
                readOnly  // ⬅ Prevent manual toggling
                className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              In Stock
            </label>
          </div>
          
          <div className="flex flex-wrap gap-4 mt-4">
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-500"
            >
              {editIndex !== null ? "Update Product" : "Add Product"}
            </button>

            <button
              type="button"
              onClick={() => {
                if (editIndex !== null) {
                  // Cancel update: clear form, exit edit mode, and hide form
                  setForm({
                    name: "",
                    price: "",
                    gender: "unisex",
                    category: "accessory",
                    inStock: true,
                    featured: false,
                    image: "",
                    description: "",
                    descriptionLong: "",
                  });
                  setEditIndex(null);
                  setShowForm(false);
                } else {
                  // Reset form only
                  setForm({
                    name: "",
                    price: "",
                    gender: "unisex",
                    category: "accessory",
                    inStock: true,
                    featured: false,
                    image: "",
                    description: "",
                    descriptionLong: "",
                  });
                }
              }}
              className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600"
            >
              {editIndex !== null ? "Cancel" : "Reset"}
            </button>
          </div>
        </form>
      )}

      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
        <input
          type="text"
          placeholder="Search products..."
          className="border p-2 rounded w-full md:w-1/3"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />
        <select
          className="border p-2 rounded w-full md:w-1/4"
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="All">All Categories</option>
          <option value="accessory">Accessory</option>
          <option value="flower">Flower</option>
          <option value="vip">Vip</option>
        </select>
        <select
          className="border p-2 rounded w-full md:w-1/4"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="All">All Statuses</option>
          <option value="In Stock">In Stock</option>
          <option value="Out of Stock">Out of Stock</option>
        </select>
      </div>

      {/* Products Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {paginated.length === 0 && (
          <p className="text-center col-span-full text-gray-500 mt-8">No products found.</p>
        )}

        {paginated.map((product, index) => (
          <div
            key={index}
            className="bg-white p-4 rounded shadow flex flex-col gap-2"
          >
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-48 object-cover rounded"
            />
            <div>
              {/* Product Name + Stock Qty Row */}
              <div className="flex items-start justify-between gap-4">
                <p className="font-bold leading-snug line-clamp-2 min-h-[2.6em]">
                  {product.name}
                </p>

                <span className="flex-shrink-0 whitespace-nowrap text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded border">
                  Stock: {product.stockQty ?? 0}
                </span>
              </div>

              {/* Price */}
              <p className="text-gray-700">${Number(product.price).toFixed(2)}</p>

              {/* Category + Gender */}
              <p className="text-sm text-gray-500">
                {(product.category === "accessory" || product.category === "vip")
                  ? `${product.category} • ${product.gender}`
                  : product.category}
              </p>

              {/* Tags */}
              <div className="mt-1 flex flex-wrap gap-2">
                {product.featured && (
                  <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">
                    Featured
                  </span>
                )}
                {product.category === "vip" && (
                  <span className="text-xs bg-yellow-500 text-white px-2 py-0.5 rounded">
                    VIP
                  </span>
                )}
                {!product.inStock ? (
                  <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded">
                    Out of Stock
                  </span>
                ) : (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                    In Stock
                  </span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between mt-2">
              <button
                onClick={() => handleEdit(products.indexOf(product))}
                className="text-blue-600 flex items-center gap-1"
              >
                <Pencil size={16} /> Edit
              </button>
              <button
                onClick={() => handleDelete(product._id)}
                className="text-red-600 flex items-center gap-1"
              >
                <Trash2 size={16} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6 space-x-2">
          <button
            className="px-3 py-1 border rounded disabled:opacity-50"
            disabled={currentPage === 1}
            onClick={() => goToPage(currentPage - 1)}
          >
            Previous
          </button>

          {[...Array(totalPages)].map((_, i) => {
            const pageNum = i + 1;
            return (
              <button
                key={pageNum}
                className={`px-3 py-1 border rounded ${
                  pageNum === currentPage ? "bg-blue-600 text-white" : ""
                }`}
                onClick={() => goToPage(pageNum)}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            className="px-3 py-1 border rounded disabled:opacity-50"
            disabled={currentPage === totalPages}
            onClick={() => goToPage(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
